import { GuideContent, GuideShots } from 'modules/video-markers/domain/interfaces/guide-content';
import { ApiError, toApiError } from 'shared/errors';

export interface VideoMarkersGuideArgs {
  /** Организация (backend `tournamentId`) — понадобится бэкенду для проверки тарифа; локальная загрузка её не читает. */
  organizerId: number;
  locale: string;
}

export interface VideoMarkersGuide {
  content: GuideContent;
  /** Имя файла скриншота → URL картинки. */
  shots: GuideShots;
}

export type VideoMarkersGuideResult = { data: VideoMarkersGuide } | { error: ApiError };

type GuideLocale = 'ru' | 'en';

/**
 * Инструкция оператора видеометок (IMGM-1607) — временно из отдельного чанка сборки.
 *
 * Отдавать её должен основной бэкенд после проверки тарифа (на проде Supabase нет storage и functions, план
 * `video-markers-guide-paywall`, фаза 7). Пока его нет, тексты и скриншоты грузятся `import()`: webpack кладёт их
 * в чанк `video-markers-guide`, который скачивается только при открытии платного роута. Это мягкий пейволл —
 * чанк лежит в сборке, и его можно найти; когда появится эндпоинт, этот файл заменит запрос к нему с тем же
 * ответом `{ content, shots }`, остальное не меняется.
 */
export async function fetchVideoMarkersGuide({ locale }: VideoMarkersGuideArgs): Promise<VideoMarkersGuideResult> {
  try {
    const content = await loadContent(locale === 'en' ? 'en' : 'ru');
    const names = Array.from(new Set(shotNames(content)));
    const urls = await Promise.all(names.map(loadShot));
    const shots: GuideShots = Object.fromEntries(names.map((name, index) => [name, urls[index]]));
    return { data: { content, shots } };
  } catch (error) {
    return { error: toApiError(error) };
  }
}

async function loadContent(locale: GuideLocale): Promise<GuideContent> {
  const module = await import(
    /* webpackChunkName: "video-markers-guide", webpackMode: "lazy-once", webpackInclude: /content\.\w+\.json$/ */
    `migrations/assets/video-markers-guide/content.${locale}.json`
  );
  return module.default;
}

async function loadShot(name: string): Promise<string> {
  const module = await import(
    /* webpackChunkName: "video-markers-guide", webpackMode: "lazy-once", webpackInclude: /\.png$/ */
    `migrations/assets/video-markers-guide/${name}`
  );
  return module.default;
}

function shotNames(content: GuideContent): string[] {
  return Object.values(content.programs).flatMap((steps) =>
    steps.flatMap((step) => (step.resultShot ? [...step.clickShots, step.resultShot] : step.clickShots)),
  );
}
